/**
 * <film-band> — the "From Soil to Cell" section.
 *
 * Behind the copy, the About film (a Mux HLS stream) autoplays muted on a loop, like the live site.
 * Pressing play opens a modal that plays the film FROM THE START with sound and the same elegant
 * controls as the About page (play/pause, scrubber, mute, fullscreen).
 *
 * TWO video elements, on purpose. The ambient one (behind the copy) plays forward forever; its buffer
 * follows its playhead, so seeking IT back to 0 re-downloads the start and stalls (MDN: seeks are only
 * instant inside a buffered range; native Safari can't retain 0). So the modal has its OWN element that
 * plays forward from 0 every open -- never a backward seek, never a re-buffer. Its stream is primed on
 * hover / tap intent, and the sharp Mux poster holds frame 0 until playback, so the open is instant and
 * crisp. While the modal is open the ambient element is paused (one active stream at a time).
 *
 * A custom element rather than an inline <script>: the Section Rendering API re-injects section HTML on
 * every theme-editor change and never re-executes script tags. Streams (and hls.js, where needed) load
 * only when the section nears the viewport / on intent, so a below-the-fold film costs nothing up front.
 */
// Wrapped in an IIFE because a section can appear more than once on a page; a second top-level class
// declaration would be a hard error.
(() => {
const HLS_CDN = 'https://cdn.jsdelivr.net/npm/hls.js@1/dist/hls.min.js';

class FilmBand extends HTMLElement {
  connectedCallback() {
    this.bgVideo = this.querySelector('[data-film-video]');
    this.modalVideo = this.querySelector('[data-film-modal-video]');
    this.openBtns = this.querySelectorAll('[data-film-open]');
    this.modal = this.querySelector('[data-film-modal]');
    this.muxId = this.dataset.mux;
    if (!this.bgVideo || !this.modalVideo || !this.muxId || !this.modal) return;

    this.stage = this.modal.querySelector('[data-film-stage]');
    this.controls = this.modal.querySelector('[data-film-controls]');
    this.scrubber = this.modal.querySelector('.film-band__scrubber-input');
    this.scrubberFill = this.modal.querySelector('.film-band__scrubber-fill');
    this.timeLabel = this.modal.querySelector('[data-film-time]');
    this.playPauseBtn = this.modal.querySelector('[data-action="playpause"]');
    this.muteBtn = this.modal.querySelector('[data-action="mute"]');
    this.fsBtn = this.modal.querySelector('[data-action="fullscreen"]');
    this.pauseIcon = this.modal.querySelector('.film-band__ctrl-pause');
    this.playIcon = this.modal.querySelector('.film-band__ctrl-play');
    this.unmutedIcon = this.modal.querySelector('.film-band__ctrl-unmuted');
    this.mutedIcon = this.modal.querySelector('.film-band__ctrl-muted');

    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.isOpen = false;
    this.hideTimer = null;

    // Both the play circle and the primary "Watch Our Story" button open the modal (neither navigates).
    this.openBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.open());
      // Prime the modal stream the moment intent shows, so it is buffered at 0 before the click lands.
      ['pointerenter', 'focus', 'touchstart'].forEach((ev) =>
        btn.addEventListener(ev, () => this.primeModal(), { passive: true }));
    });
    this.modal.querySelectorAll('[data-film-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.onKeydown = (e) => this.handleKeydown(e);

    this.wireControls();

    // Relocate the modal to <body> (root stacking context) so it clears the sticky header. Refs above
    // were captured first, so moving the node keeps them valid.
    if (this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
      this.modalRelocated = true;
    }

    // Attach the ambient stream (and start its muted loop) once the section nears the viewport.
    this.io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.io.disconnect();
      // Reduced motion: the ambient video is display:none (CSS) and the section's lazy background
      // image is already the still, so neither the poster nor the stream nor hls.js is worth a single
      // byte here. This is the ambient loop only -- pressing play still loads and plays the film with
      // sound, because that is explicit intent, not motion the visitor didn't ask for.
      if (this.motion.matches) return;
      // Poster first (outside loadAmbient, which bails on save-data -- those users still want the still
      // frame), then the stream.
      this.revealPoster(this.bgVideo);
      this.loadAmbient();
      // 2600px, up from 1200 (and 400 before that): Joey wants the film not just present but already
      // at full ladder height by the time it scrolls in. Roughly two viewports of runway, which buys
      // hls.js the few segments it needs to confirm the 5Mbps opening estimate and settle. The band
      // sits ~1685px below the fold, so on the homepage this now fires essentially at page load --
      // deliberately, since it is one section on one page and LCP is already long done by then.
    }, { rootMargin: '2600px 0px' });
    this.io.observe(this);
  }

  disconnectedCallback() {
    this.io?.disconnect();
    clearTimeout(this.closeTimer);
    document.removeEventListener('keydown', this.onKeydown);
    document.body.classList.remove('film-band-modal-open');
    if (this.modalRelocated) this.modal?.remove();
  }

  // --- Stream loading --------------------------------------------------------

  /** Attach the Mux HLS stream to a video. `onReady` fires once the stream is ready to play. */
  loadStream(video, opts = {}) {
    if (video.__filmLoaded) return;
    video.__filmLoaded = true;
    // rendition_order=desc: highest rendition first in the manifest, so the NATIVE path (Safari, and
    // current Chrome) opens at the top of the ladder instead of climbing up from the bottom. hls.js
    // already picks via ABR. See Mux "modify playback behavior".
    //
    // min_resolution=720p on the AMBIENT loop only. Verified against the live manifest: the ladder is
    // 1080@9.2, 1080@6.9, 720@3.6, 540@2.3, 360@1.2, and this leaves the top three. It sets a quality
    // floor (the loop can never open at 360p soft behind the copy) and, because Mux caches segments for
    // 7 days but the manifest for none, fewer rungs means a repeat visit is far likelier to land on a
    // rung it already has rather than re-download a new one.
    //
    // NOT on the modal. That is a film someone deliberately pressed play on: it gets the full ladder so
    // a weak connection degrades instead of stalling. The ambient loop can afford the opposite trade
    // because it is decorative and muted -- the poster holds and only motion is lost.
    const floor = opts.ambient ? '&min_resolution=720p' : '';
    const url = 'https://stream.mux.com/' + this.muxId + '.m3u8?rendition_order=desc' + floor;
    const onReady = opts.onReady || (() => {});

    // 2026-09-24: built-in HLS only when the browser has NO Media Source Extensions (older iPhones).
    // Chrome 14x now answers canPlayType('application/vnd.apple.mpegurl') with 'maybe' but fails
    // to play these Mux streams (MEDIA_ERR_SRC_NOT_SUPPORTED), so trusting canPlayType broke the
    // film on Chrome. hls.js plays them wherever MSE exists (Chrome, Firefox, Edge, desktop Safari,
    // iOS 17.1+ via ManagedMediaSource); that is also hls.js's own recommended order.
    if (!(window.MediaSource || window.ManagedMediaSource) && video.canPlayType('application/vnd.apple.mpegurl')) {
      if (opts.preloadAuto) video.preload = 'auto';        // buffer the start while paused (priming)
      video.src = url;                                     // Safari plays HLS natively
      onReady();
    } else if (window.Hls && window.Hls.isSupported()) {
      this.attachHls(video, url, opts, onReady);
    } else {
      // Shared with mux-bg.js via window, because the homepage runs both players and both need this
      // same ~1MB library. Each file used to append its own <script> for the identical URL: the HTTP
      // cache deduped the download, but two script elements both execute, so the library was parsed
      // and evaluated twice on the main thread. Parking the promise on window means whichever player
      // arrives first pays for it and the other awaits the same load. Also closes the race where both
      // observers fire in the same tick, both see window.Hls undefined, and both inject.
      (window.__zumaHlsLib ||= new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = HLS_CDN;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      }))
        .then(() => { if (window.Hls && window.Hls.isSupported()) this.attachHls(video, url, opts, onReady); })
        .catch(() => {});   // CDN blocked/offline: the poster image still holds
    }
  }

  attachHls(video, url, opts, onReady) {
    // backBufferLength Infinity keeps played content so a replay-from-0 stays inside the buffer (no
    // re-download) on hls.js browsers; capLevelToPlayerSize defaults false so quality isn't capped low.
    //
    // abrEwmaDefaultEstimate is the one that actually governs opening quality here, and its default is
    // the trap: 500kbps. On a cold load hls.js has no bandwidth history, so it trusts that estimate,
    // picks a rendition sized for a 500kbps line, and only climbs the ladder after a few segments have
    // proven the connection is faster. That is why the film opened soft even though the manifest asks
    // for rendition_order=desc -- desc only decides where a NATIVE player starts (Safari/Chrome), and
    // ABR overrules it on the hls.js path entirely.
    //
    // 5Mbps says "assume broadband until measured otherwise". It is a starting guess, not a cap: real
    // throughput replaces it within a segment or two, so a genuinely slow connection still drops down.
    // Safe here specifically because we load a viewport-plus ahead of the band, so there is runway to
    // recover if the guess was wrong. Data-saver and 2g never reach this code at all.
    const hls = new window.Hls({
      maxMaxBufferLength: 30,
      startLevel: -1,
      abrEwmaDefaultEstimate: 5_000_000,
      backBufferLength: opts.keepBackBuffer ? Infinity : 90,
    });
    hls.loadSource(url);
    hls.attachMedia(video);
    hls.on(window.Hls.Events.MANIFEST_PARSED, () => onReady());
    video.__hls = hls;
  }

  /** `preload="none"` does NOT cover `poster`: the browser fetches a poster URL as soon as the <video>
   *  lays out, so a hardcoded poster= downloaded both Mux thumbnails (1600px + 1920px) on every page
   *  load even though this band sits ~1700px below the fold. The URLs are parked in data-poster and
   *  promoted here -- on approach for the ambient frame, on open-intent for the modal. */
  revealPoster(video) {
    if (!video?.dataset.poster) return;
    video.poster = video.dataset.poster;
    delete video.dataset.poster;   // promote once
  }

  loadAmbient() {
    // Skip the decorative ambient stream (and, on non-Safari, the ~1MB hls.js) for data-saver or slow
    // connections. The poster holds; pressing play still loads the film on demand (that's user intent).
    if (this.saveData()) {
      // Signal the 2.2.2 control that there will never be a stream, so it hides instead of offering to
      // pause a poster. Reduced motion is covered by CSS; save-data is not a media query.
      this.bgVideo.__ambientUnavailable = true;
      document.querySelectorAll('ambient-pause').forEach((el) => el.refresh?.());
      return;
    }
    this.loadStream(this.bgVideo, { ambient: true, onReady: () => this.startAmbient() });
  }

  /** Data-saver enabled, or a 2g-class effective connection. */
  saveData() {
    const c = navigator.connection;
    return !!(c && (c.saveData || c.effectiveType === '2g' || c.effectiveType === 'slow-2g'));
  }

  /** Muted ambient loop on the background element, unless reduced motion (then the poster holds). */
  startAmbient() {
    if (this.isOpen) return;                               // the modal owns playback while it is open
    // The visitor pressed the 2.2.2 pause control (assets/ambient-pause.js). Every path back into
    // playback has to check this, or the next stream-ready / modal-close would quietly undo them.
    if (this.bgVideo.__ambientPaused) return;
    this.bgVideo.muted = true;
    this.bgVideo.loop = true;
    if (!this.motion.matches) this.bgVideo.play().catch(() => {});
  }

  /** Load + buffer the modal's own stream at 0, paused. Idempotent. If the modal is already open when
   *  the stream becomes ready (cold open on hls.js, where attach is async), start it from 0. */
  primeModal() {
    this.revealPoster(this.modalVideo);   // fetched on open-intent (hover/focus/tap), not page load
    this.loadStream(this.modalVideo, {
      preloadAuto: true,
      keepBackBuffer: true,
      onReady: () => { if (this.isOpen) this.playModalFromStart(); },
    });
  }

  /** Restart the modal video from the beginning, playing forward with sound. */
  playModalFromStart() {
    this.modalVideo.loop = false;
    this.modalVideo.muted = false;
    try { this.modalVideo.currentTime = 0; } catch (e) {}
    this.modalVideo.play().catch(() => {});
  }

  // --- Modal -----------------------------------------------------------------

  open() {
    if (this.isOpen) return;
    clearTimeout(this.closeTimer);                        // cancel a pending close-finish (rapid reopen)
    this.isOpen = true;
    this.primeModal();

    this.lastFocus = document.activeElement;
    this.modal.hidden = false;
    void this.modal.offsetWidth;                          // reflow so opacity transitions from 0 -> 1
    this.modal.classList.add('is-open');                  // fade in
    document.body.classList.add('film-band-modal-open');
    document.addEventListener('keydown', this.onKeydown);

    this.bgVideo.pause();                                  // one active stream at a time
    // Restart from the beginning, playing FORWARD (no backward seek -> no re-buffer). The poster holds
    // a sharp frame until the video paints, so there is no blur. If the stream is not ready yet (cold
    // open on hls.js), primeModal's onReady starts it once the manifest parses.
    this.playModalFromStart();

    this.setPlayingIcon(true);
    this.setMutedIcon(false);
    this.controls.classList.remove('film-band__controls--hidden');
    this.showControls();
    (this.modal.querySelector('.film-band__modal-close') || this.stage).focus();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    clearTimeout(this.hideTimer);
    document.removeEventListener('keydown', this.onKeydown);
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});

    this.modal.classList.remove('is-open');                // fade out

    // Tear down once the fade finishes (or immediately under reduced motion).
    const finish = () => {
      this.modal.hidden = true;
      document.body.classList.remove('film-band-modal-open');
      this.modalVideo.pause();
      try { this.modalVideo.currentTime = 0; } catch (e) {}  // reset so the next open starts at 0
      this.startAmbient();                                 // resume the muted ambient loop
    };
    clearTimeout(this.closeTimer);
    if (this.motion.matches) finish();
    else this.closeTimer = setTimeout(finish, 300);        // just past the 280ms opacity fade

    this.lastFocus?.focus?.();
  }

  handleKeydown(e) {
    if (e.key === 'Escape') { this.close(); return; }
    if (e.key === 'Tab') this.trapFocus(e);
  }

  trapFocus(e) {
    const focusables = this.modal.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  // --- Controls (ported from the live About player) --------------------------

  wireControls() {
    this.playPauseBtn?.addEventListener('click', () => this.togglePlay());
    this.muteBtn?.addEventListener('click', () => this.toggleMute());
    this.fsBtn?.addEventListener('click', () => this.toggleFullscreen());

    this.scrubber?.addEventListener('input', () => {
      if (this.modalVideo.duration) this.modalVideo.currentTime = (parseFloat(this.scrubber.value) / 100) * this.modalVideo.duration;
    });

    this.modalVideo.addEventListener('timeupdate', () => {
      if (!this.isOpen || !this.modalVideo.duration) return;
      const pct = (this.modalVideo.currentTime / this.modalVideo.duration) * 100;
      if (this.scrubber) this.scrubber.value = pct;
      if (this.scrubberFill) this.scrubberFill.style.width = pct + '%';
      if (this.timeLabel) this.timeLabel.textContent = this.formatTime(this.modalVideo.currentTime);
    });

    this.modalVideo.addEventListener('click', () => { if (this.isOpen) this.togglePlay(); });
    this.modalVideo.addEventListener('ended', () => { if (this.isOpen) this.close(); });

    this.stage.addEventListener('mousemove', () => this.showControls());
    this.stage.addEventListener('touchstart', () => this.showControls(), { passive: true });
    this.controls.addEventListener('mouseenter', () => { clearTimeout(this.hideTimer); this.controls.classList.remove('film-band__controls--hidden'); });
    this.controls.addEventListener('mouseleave', () => this.showControls());

    // Keyboard equivalents of mouseenter/mouseleave. Without these the controls auto-hide 1800ms after
    // they appear, no matter what has focus -- so a keyboard visitor tabs to Play, the control fades to
    // opacity 0 and pointer-events: none under them, and focus is now on something invisible and
    // unusable. showControls() below refuses to start the timer while focus is inside.
    this.controls.addEventListener('focusin', () => this.showControls());
    this.controls.addEventListener('focusout', () => this.showControls());
  }

  /** True while keyboard focus is inside the control bar. */
  get controlsFocused() {
    return !!this.controls && this.controls.contains(document.activeElement);
  }

  togglePlay() {
    if (this.modalVideo.paused) { this.modalVideo.play().catch(() => {}); this.setPlayingIcon(true); this.showControls(); }
    else { this.modalVideo.pause(); this.setPlayingIcon(false); clearTimeout(this.hideTimer); this.controls.classList.remove('film-band__controls--hidden'); }
  }

  toggleMute() {
    this.modalVideo.muted = !this.modalVideo.muted;
    this.setMutedIcon(this.modalVideo.muted);
  }

  toggleFullscreen() {
    if (document.fullscreenElement) { document.exitFullscreen().catch(() => {}); }
    else if (this.stage.requestFullscreen) { this.stage.requestFullscreen().catch(() => {}); }
    else if (this.modalVideo.webkitEnterFullscreen) { this.modalVideo.webkitEnterFullscreen(); }
  }

  showControls() {
    if (!this.isOpen) return;
    this.controls.classList.remove('film-band__controls--hidden');
    clearTimeout(this.hideTimer);
    // Never hide the bar out from under keyboard focus (WCAG 2.4.7 / 2.4.11): the focused control would
    // still be focused, but invisible and pointer-events: none.
    if (!this.modalVideo.paused && !this.controlsFocused) {
      this.hideTimer = setTimeout(() => this.controls.classList.add('film-band__controls--hidden'), 1800);
    }
  }

  setPlayingIcon(playing) {
    if (this.pauseIcon) this.pauseIcon.style.display = playing ? '' : 'none';
    if (this.playIcon) this.playIcon.style.display = playing ? 'none' : '';
    this.playPauseBtn?.setAttribute('aria-label', playing ? 'Pause' : 'Play');
  }

  setMutedIcon(muted) {
    if (this.unmutedIcon) this.unmutedIcon.style.display = muted ? 'none' : '';
    if (this.mutedIcon) this.mutedIcon.style.display = muted ? '' : 'none';
    this.muteBtn?.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
  }

  formatTime(sec) {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }
}

if (!customElements.get('film-band')) customElements.define('film-band', FilmBand);
})();
